import { KeyboardArrowLeft } from "@mui/icons-material"
import { Grid, IconButton, Theme, Typography } from "@mui/material"
import { makeStyles } from "@mui/styles"
import { EcoSpinner, GenericDialog } from "components"
import { useDirtyForm } from "hooks"
import { Suspense, useEffect, useState } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { GenericTabs, GenericTabsMenuItemProps, GenericTabsMenuItemType } from "."

/**
 * Ecomundo screen tabs viewing implementation based on "react-router-dom" Outlet
 */
export const GenericTabsContainer = (props: GenericTabsContainerProps) => {

    const classes = useStyles()
    const navigate = useNavigate()
    const [nextUrl, setNextUrl] = useState<string>()
    const { hasDirty, resetDirty } = useDirtyForm()

    useEffect(() => {
        resetDirty()
        return () => {
            resetDirty()
        }
    }, [])

    const handleBackClick = () => {
        const path = props.backUrl || '-1'
        if (hasDirty) {
            return setNextUrl(path)
        }

        return navigate(path)
    }

    const handleNavigation = (menu?: GenericTabsMenuItemType) => {
        setNextUrl(menu?.path)
        return !hasDirty
    }

    const handleNavigationConfirmed = () => {
        navigate(nextUrl!)
        setNextUrl(undefined)
    }

    return (
        <Grid container>
            <Grid item xs={12} mb={3}>
                <div className={`${classes.headerTitle} `}>
                    <Typography 
                        variant='h1' 
                        color="primary"
                        fontSize={32}
                        fontWeight="bold"
                        className={`eco-main-title`}>
                        { props.title }
                    </Typography>
                </div>
            </Grid>

            <Grid item xs={12} mb={5}>
                <Typography>
                    <IconButton onClick={handleBackClick}>
                        <KeyboardArrowLeft />
                    </IconButton>
                    <span> | </span>
                    <span style={{ marginLeft: 15 }}> {props.subTitle} </span>
                </Typography>
            </Grid>

            <GenericTabs
                menus={props?.menus || []}
                GenericTabsMenuProps={{
                    onNavigate: handleNavigation
                }}>
                <Suspense 
                    fallback={props.suspenseFallback || <EcoSpinner style={{ fontSize: 64 }} />}>
                    <Outlet />
                </Suspense>
            </GenericTabs>

            <GenericDialog
                open={hasDirty && nextUrl !== undefined}
                dialog={{
                    title: 'Confirmation',
                    text: 'Voulez-vous continuer ?',
                    btnOk: {
                        label: 'Continuer'
                    },
                    btnCancel: {
                        variant: 'text',
                        label: 'Annuler'
                    },
                }}
                maxWidth="sm"
                onSubmit={handleNavigationConfirmed}
                onClose={() => setNextUrl(undefined)}
                />
        </Grid>
    )
}

const useStyles = makeStyles((theme: Theme) => ({
    headerTitle: {
        margin: '0',
        textTransform: 'uppercase',
        fontFamily: '"Montserrat", sans-serif',
        position: 'relative',
        '& .eco-main-title': {
            margin: '0 0 10px 0',
            color: '#2C72AB',
            '&:before': {
                content: '""',
                position: 'absolute',
                left: '0',
                bottom: '-5px',
                height: '1px',
                width: '50px',
                borderBottom: `3px solid ${theme.palette.secondary.main}`,
            },
        },
    }
}));

export type GenericTabsContainerProps = {
    title?: string,
    subTitle?: string,
    menus?: Array<GenericTabsMenuItemProps>
    /**
     * Link to redirect when arrow left is clicked
     */
    backUrl?: string
    suspenseFallback?: React.ReactElement
}
